import { memo, useState } from "react";
import { Play, Heart, Maximize2, Loader2 } from "lucide-react";
import { Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";

interface AssetPreviewProps {
  type: "video" | "image";
  thumbnail: string | null;
  title?: string | null;
  isFavorite: boolean;
  isProcessing?: boolean;
  onPlay: () => void;
  onToggleFavorite: () => void;
}

/** Video loading shimmer overlay */
function VideoLoadingOverlay() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
      
      {/* Center loader */}
      <div className="relative flex flex-col items-center gap-3">
        {/* Animated rings */}
        <div className="relative size-16">
          <div className="absolute inset-0 rounded-full border-2 border-violet-500/30 animate-ping" />
          <div className="absolute inset-1 rounded-full border-2 border-violet-400/20 animate-ping [animation-delay:300ms]" />
          <div className="absolute inset-2 rounded-full border-2 border-violet-300/10 animate-ping [animation-delay:600ms]" />
          
          {/* Center icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="p-2.5 rounded-full bg-violet-500/20 backdrop-blur-sm border border-violet-500/40">
              <Play className="size-5 text-violet-400 fill-violet-400/50" />
            </div>
          </div>
        </div>
        
        {/* Loading text with dots animation */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-zinc-500 font-medium">Loading video</span>
          <span className="flex gap-0.5">
            <span className="size-1 rounded-full bg-violet-400 animate-bounce [animation-delay:0ms]" />
            <span className="size-1 rounded-full bg-violet-400 animate-bounce [animation-delay:150ms]" />
            <span className="size-1 rounded-full bg-violet-400 animate-bounce [animation-delay:300ms]" />
          </span>
        </div>
      </div>
      
      {/* Corner decorations */}
      <div className="absolute top-3 left-3 size-8 border-l-2 border-t-2 border-violet-500/20 rounded-tl-lg" />
      <div className="absolute top-3 right-3 size-8 border-r-2 border-t-2 border-violet-500/20 rounded-tr-lg" />
      <div className="absolute bottom-3 left-3 size-8 border-l-2 border-b-2 border-violet-500/20 rounded-bl-lg" />
      <div className="absolute bottom-3 right-3 size-8 border-r-2 border-b-2 border-violet-500/20 rounded-br-lg" />
    </div>
  );
}

export const AssetPreview = memo(function AssetPreview({
  type,
  thumbnail,
  title,
  isFavorite,
  isProcessing = false,
  onPlay,
  onToggleFavorite,
}: AssetPreviewProps) {
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isImageLoading, setIsImageLoading] = useState(true);

  return (
    <div
      className={cn(
        "relative bg-black flex items-center justify-center",
        "h-[200px] md:h-auto md:aspect-video",
        !isProcessing && "cursor-pointer",
      )}
      onClick={isProcessing ? undefined : onPlay}
    >
      {isProcessing || !thumbnail ? (
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-violet-500/20 animate-ping" />
            <div className="relative p-3 rounded-full bg-violet-500/20 border border-violet-500/30">
              <Loader2 className="size-6 text-violet-400 animate-spin" />
            </div>
          </div>
          <span className="text-xs text-[#888]">Generating...</span>
        </div>
      ) : type === "video" ? (
        <>
          {isVideoLoading && <VideoLoadingOverlay />}
          <video
            src={`${thumbnail}#t=0.001`}
            muted
            playsInline
            preload="metadata"
            className={cn(
              "max-w-full max-h-full object-contain transition-opacity duration-300",
              isVideoLoading ? "opacity-0" : "opacity-100"
            )}
            onLoadedData={() => setIsVideoLoading(false)}
          />
        </>
      ) : (
        <>
          {isImageLoading && (
            <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 animate-pulse" />
          )}
          <img
            src={thumbnail}
            alt={title || "Asset"}
            loading="lazy"
            className={cn(
              "max-w-full max-h-full object-contain transition-opacity duration-300",
              isImageLoading ? "opacity-0" : "opacity-100"
            )}
            onLoad={() => setIsImageLoading(false)}
          />
        </>
      )}

      {!isProcessing && (
        <>
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none hidden md:block" />
          <div className="absolute top-3 left-3 z-10 md:hidden">
            <div className="p-2 rounded-lg bg-black/50 backdrop-blur-sm">
              <Maximize2 className="size-4 text-white/80" />
            </div>
          </div>
          <div className="absolute inset-0 hidden md:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="p-4 rounded-xl bg-black/40 border border-white/20">
              {type === "video" ? (
                <Play className="size-6 text-white fill-white" />
              ) : (
                <Maximize2 className="size-6 text-white" />
              )}
            </div>
          </div>
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <Tooltip
              title={isFavorite ? "Unfavorite" : "Favorite"}
              position="bottom"
              align="right"
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite();
                }}
                className={cn(
                  "p-2 rounded-lg bg-black/50 backdrop-blur-sm transition-colors",
                  isFavorite
                    ? "text-red-500 hover:text-red-400"
                    : "text-white/80 hover:text-white hover:bg-black/70",
                )}
              >
                <Heart className={cn("size-4", isFavorite && "fill-current")} />
              </button>
            </Tooltip>
          </div>
        </>
      )}
      <div className="absolute bottom-3 left-3 pointer-events-none">
        <span className="px-2 py-1 rounded-md bg-black/50 backdrop-blur-sm text-xs font-medium text-white/80">
          {type === "video" ? "Video" : "Image"}
        </span>
      </div>
    </div>
  );
});
